import { useMemo } from 'react';
import { useGetManyReference, useGetMany } from 'react-admin';
import { Funeral, Client } from '../../types';
import { getClientColor } from '../components/ClientFilterBar';

/** Normalize phone to digits-only for comparison */
function normalizePhone(phone?: string): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  return digits;
}

export interface ColoredClient {
  id: number;
  name: string;
  color: string;
  phone_number: string;
  email?: string | null;
}

export interface FuneralClients {
  coloredClients: ColoredClient[];
  /** client id → client id. Lets a feed event's recipient_id resolve to a client. */
  clientByRecipientId: Map<number, number>;
  /** normalized phone or lowercased email → client id */
  clientByContactDetail: Map<string, number>;
  clientColorMap: Map<number, string>;
}

/**
 * Every client attached to a funeral, colour-assigned and indexed for feed
 * lookups. Extracted from `FuneralShowRedesign` so the docked inbox can mount
 * `FuneralShowFeed` for a case without re-implementing the same joins.
 *
 * Colour order is stable: primary client first, then paying / authorized /
 * declarant, then the join table — so a person keeps their colour between the
 * case page and the inbox.
 */
export function useFuneralClients(record?: Funeral | null): FuneralClients {
  const { data: funeralClients, isLoading: isLoadingFC } = useGetManyReference(
    'funeral_clients',
    { target: 'funeral_id', id: record?.id ?? 0 },
    { enabled: !!record?.id },
  );

  const clientIds = useMemo(() => {
    const ids: number[] = [];
    const seen = new Set<number>();
    const addId = (id: unknown) => {
      if (!id) return;
      const num = Number(id);
      if (!seen.has(num)) {
        seen.add(num);
        ids.push(num);
      }
    };
    addId(record?.primary_client_id);
    addId(record?.paying_client_id);
    addId(record?.authorized_client_id);
    addId(record?.declarant_client_id);
    if (funeralClients) {
      for (const fc of funeralClients) {
        addId(fc.client_id);
      }
    }
    return ids;
  }, [
    funeralClients,
    record?.primary_client_id,
    record?.paying_client_id,
    record?.authorized_client_id,
    record?.declarant_client_id,
  ]);

  const { data: clients } = useGetMany<Client>(
    'clients',
    { ids: clientIds },
    { enabled: !isLoadingFC && clientIds.length > 0 },
  );

  return useMemo(() => {
    if (!clients || clients.length === 0) {
      return {
        coloredClients: [],
        clientByRecipientId: new Map<number, number>(),
        clientByContactDetail: new Map<string, number>(),
        clientColorMap: new Map<number, string>(),
      };
    }

    const sorted = [...clients].sort((a, b) => {
      if (a.id === record?.primary_client_id) return -1;
      if (b.id === record?.primary_client_id) return 1;
      return (a.id as number) - (b.id as number);
    });

    const coloredClients = sorted.map((c, i) => ({
      id: c.id as number,
      name: c.name,
      color: getClientColor(i),
      phone_number: c.phone_number || '',
      email: c.email || null,
    }));

    const clientByRecipientId = new Map<number, number>();
    const clientByContactDetail = new Map<string, number>();
    const clientColorMap = new Map<number, string>();

    for (const c of sorted) {
      const cid = c.id as number;
      clientByRecipientId.set(cid, cid);
      if (c.phone_number) clientByContactDetail.set(normalizePhone(c.phone_number), cid);
      if (c.email) clientByContactDetail.set(c.email.trim().toLowerCase(), cid);
    }
    for (const c of coloredClients) {
      clientColorMap.set(c.id, c.color);
    }

    return { coloredClients, clientByRecipientId, clientByContactDetail, clientColorMap };
  }, [clients, record?.primary_client_id]);
}
